import RetentionAPI from '../api/retention';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import mutationTypes from 'dashboard/store/mutation-types';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';

const { isImpersonating } = useImpersonation();

const STATS_DEBOUNCE_MS = 500;
let statsDebounceTimer = null;

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = new Map();
    this.events = {
      'retention.changed': this.onRetentionChanged,
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'team.changed': this.onTeamChanged,
      'support_line.changed': this.onSupportLineChanged,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onRetentionChanged = data => {
    if (!this.isAValidEvent(data)) return;
    if (data.conversation_id) {
      this.app.$store.commit(
        mutationTypes.DELETE_CONVERSATION,
        data.conversation_id
      );
      this.fetchConversationStats();
    }
    this.app.$store.dispatch('notifications/get');
    emitter.emit('retention.changed', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => {
    AuthAPI.logout().catch(() => {});
    try {
      deleteIndexedDBOnLogout();
      clearCookiesOnLogout();
    } finally {
      window.location.replace('/app/login');
    }
  };

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTeamChanged = data => {
    const { id } = data;
    if (id) {
      this.app.$store.dispatch('updateConversation', data);
    }
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping.get(conversationId);
    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping.delete(conversationId);
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    this.CancelTyping.set(
      conversationId,
      setTimeout(() => {
        this.onTypingOff({ conversation, user });
      }, 30000)
    );
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    if (statsDebounceTimer) {
      clearTimeout(statsDebounceTimer);
    }
    statsDebounceTimer = setTimeout(() => {
      emitter.emit('fetch_conversation_stats');
      statsDebounceTimer = null;
    }, STATS_DEBOUNCE_MS);
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
    this.app.$store.dispatch('updateContactInConversations', data);
  };

  onNotificationCreated = async data => {
    if (data.notification?.retention_url) {
      if (!this.isAValidEvent(data)) return;
      try {
        const { data: access } = await RetentionAPI.capabilities();
        if (!access.member) return;
      } catch {
        return;
      }
      DashboardAudioNotificationHelper.onRetentionNotification();
      emitter.emit('retention.changed', {
        account_id: data.account_id,
        conversation_id: data.notification.primary_actor?.id,
      });
    }
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onSupportLineChanged = data => {
    this.app.$store.dispatch('accounts/applySupportLineChange', data);
    this.fetchConversationStats();
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

let currentConnector = null;

export default {
  init(store, pubsubToken) {
    if (currentConnector) {
      currentConnector.disconnect();
      currentConnector = null;
    }
    currentConnector = new ActionCableConnector({ $store: store }, pubsubToken);
    return currentConnector;
  },
  disconnect() {
    if (currentConnector) {
      currentConnector.disconnect();
      currentConnector = null;
    }
  },
};
